import { useEffect, useState } from 'react';
import * as ops from '../../domain/projectOps';
import type { Track, TrackEffects } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Button, Panel } from '../ui';

/** Logarithmic slider mapping for the filter cutoff (80 Hz – 20 kHz). */
const cutoffToSlider = (hz: number) => Math.round((Math.log(hz / 80) / Math.log(20000 / 80)) * 100);
const sliderToCutoff = (v: number) => Math.round(80 * (20000 / 80) ** (v / 100));
const fmtHz = (hz: number) => (hz >= 1000 ? `${(hz / 1000).toFixed(hz >= 10000 ? 0 : 1)}k` : `${hz}`);
const fmtPan = (pan: number) => (pan === 0 ? 'C' : `${Math.round(Math.abs(pan) * 100)}${pan < 0 ? 'L' : 'R'}`);

function Slider({
  id,
  label,
  value,
  min,
  max,
  step = 1,
  display,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  display: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <label htmlFor={id} className="flex justify-between gap-1 text-[11px] text-zinc-400">
        <span>{label}</span>
        <span className="font-mono text-zinc-300">{display}</span>
      </label>
      <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full" />
    </div>
  );
}

function ChannelStrip({ track, audible }: { track: Track; audible: boolean }) {
  const { update } = useProject();
  const i = track.instrument;
  const fx = (key: keyof TrackEffects, value: number) => update((p) => ops.setEffect(p, i, key, value));
  return (
    <li
      className={`grid grid-cols-2 gap-x-3 gap-y-2 rounded-lg border border-zinc-800 p-2.5 sm:grid-cols-[8rem_repeat(6,minmax(0,1fr))] sm:items-end ${audible ? 'bg-zinc-900/60' : 'bg-zinc-950/60 opacity-70'}`}
      aria-label={`${track.name} channel`}
      data-testid={`channel-${i}`}
    >
      <div className="col-span-2 flex items-center justify-between gap-2 sm:col-span-1">
        <span className="truncate text-sm font-medium">{track.name}</span>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={track.muted ? 'danger' : 'ghost'}
            aria-pressed={track.muted}
            aria-label={`Mute ${track.name}`}
            className="w-8 px-0"
            onClick={() => update((p) => ops.setMuted(p, i, !track.muted))}
          >
            M
          </Button>
          <Button
            size="sm"
            variant={track.solo ? 'primary' : 'ghost'}
            aria-pressed={track.solo}
            aria-label={`Solo ${track.name}`}
            className="w-8 px-0"
            onClick={() => update((p) => ops.setSolo(p, i, !track.solo))}
          >
            S
          </Button>
        </div>
      </div>
      <Slider id={`vol-${i}`} label="Volume" value={track.volumeDb} min={-40} max={6} step={0.5} display={`${track.volumeDb} dB`} onChange={(v) => update((p) => ops.setTrackVolume(p, i, v))} />
      <Slider id={`pan-${i}`} label="Pan" value={Math.round(track.pan * 100)} min={-100} max={100} display={fmtPan(track.pan)} onChange={(v) => update((p) => ops.setPan(p, i, v / 100))} />
      <Slider id={`drive-${i}`} label="Drive" value={Math.round(track.effects.drive * 100)} min={0} max={100} display={`${Math.round(track.effects.drive * 100)}%`} onChange={(v) => fx('drive', v / 100)} />
      <Slider id={`cutoff-${i}`} label="Filter" value={cutoffToSlider(track.effects.cutoffHz)} min={0} max={100} display={fmtHz(track.effects.cutoffHz)} onChange={(v) => fx('cutoffHz', sliderToCutoff(v))} />
      <Slider id={`delay-${i}`} label="Delay" value={Math.round(track.effects.delaySend * 100)} min={0} max={100} display={`${Math.round(track.effects.delaySend * 100)}%`} onChange={(v) => fx('delaySend', v / 100)} />
      <Slider id={`reverb-${i}`} label="Reverb" value={Math.round(track.effects.reverbSend * 100)} min={0} max={100} display={`${Math.round(track.effects.reverbSend * 100)}%`} onChange={(v) => fx('reverbSend', v / 100)} />
    </li>
  );
}

function MasterMeter() {
  const { audio } = useServices();
  const engine = useObservable(audio.state);
  const [level, setLevel] = useState(-Infinity);
  const [peakHold, setPeakHold] = useState(false);
  useEffect(() => {
    if (engine.status !== 'playing') return;
    let frame = 0;
    const loop = () => {
      const l = audio.getLevel();
      setLevel(l);
      if (l > -1) setPeakHold(true);
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frame);
      setLevel(-Infinity);
    };
  }, [audio, engine.status]);
  const shown = Number.isFinite(level) ? Math.max(-60, Math.min(0, level)) : -60;
  return (
    <div className="flex min-w-48 flex-1 flex-col gap-1">
      <label htmlFor="master-meter" className="flex justify-between text-xs text-zinc-300">
        <span>Output {engine.status === 'playing' ? `${shown.toFixed(0)} dBFS` : '(stopped)'}</span>
        {peakHold && (
          <button type="button" className="rounded bg-red-700 px-1.5 text-[11px] font-semibold text-white" onClick={() => setPeakHold(false)} title="Output peaked above −1 dBFS. Click to reset.">
            PEAK
          </button>
        )}
      </label>
      <meter id="master-meter" min={-60} max={0} low={-18} high={-6} optimum={-24} value={shown} className="h-3 w-full" />
    </div>
  );
}

export function Mixer() {
  const { project, update } = useProject();
  const audible = ops.audibleInstruments(project.tracks, project.audioBus);
  const anySolo = project.tracks.some((t) => t.solo);
  return (
    <Panel
      title="Mixer"
      actions={
        anySolo ? (
          <Button size="sm" onClick={() => update((p) => p.tracks.reduce((acc, t) => (t.solo ? ops.setSolo(acc, t.instrument, false) : acc), p))}>
            Clear solo
          </Button>
        ) : undefined
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-4 rounded-lg border border-zinc-800 bg-zinc-950/60 p-2.5">
        <div className="flex min-w-48 flex-col gap-1">
          <label htmlFor="master-volume" className="text-xs font-medium text-zinc-300">
            Master gain {project.settings.masterVolumeDb} dB
          </label>
          <input
            id="master-volume"
            type="range"
            min={-40}
            max={0}
            step={0.5}
            value={project.settings.masterVolumeDb}
            onChange={(e) => update((p) => ops.setMasterVolume(p, Number(e.target.value)))}
          />
        </div>
        <MasterMeter />
        <p className="basis-full text-xs text-zinc-400">
          The master bus runs through a gentle compressor, a limiter and a soft clipper that keeps the output below full scale; WAV exports are additionally scaled down if any peak would still exceed −0.3 dBFS. This is clipping protection, not mastering.
        </p>
      </div>
      <ul className="flex flex-col gap-2" aria-label="Channels">
        {project.tracks.map((t) => (
          <ChannelStrip key={t.id} track={t} audible={audible.has(t.instrument)} />
        ))}
      </ul>
    </Panel>
  );
}
